import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Trash2, Loader2, RefreshCw } from 'lucide-react';

type Row = { id: string; query: string; store: string; created_at: string; expires_at: string };

export default function AdminCache() {
  const { toast } = useToast();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('price_cache')
      .select('id, query, store, created_at, expires_at')
      .order('created_at', { ascending: false })
      .limit(200);
    setRows((data as Row[]) || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const removeOne = async (id: string) => {
    setBusy(id);
    const { error } = await supabase.from('price_cache').delete().eq('id', id);
    if (error) toast({ title: 'Failed', description: error.message, variant: 'destructive' });
    else { toast({ title: 'Cache entry removed' }); load(); }
    setBusy(null);
  };

  const clearExpired = async () => {
    const { error } = await supabase.from('price_cache').delete().lt('expires_at', new Date().toISOString());
    if (error) toast({ title: 'Failed', description: error.message, variant: 'destructive' });
    else { toast({ title: 'Expired entries cleared' }); load(); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Price Cache ({rows.length})</h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-3.5 w-3.5 mr-1" />Refresh</Button>
          <Button variant="destructive" size="sm" onClick={clearExpired}>Clear expired</Button>
        </div>
      </div>
      <div className="rounded-lg border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50"><tr className="text-left"><th className="p-3">Query</th><th className="p-3">Store</th><th className="p-3">Cached</th><th className="p-3">Expires</th><th className="p-3 text-right">Actions</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="p-6 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">Cache is empty</td></tr>
            ) : rows.map((r) => {
              const expired = new Date(r.expires_at) < new Date();
              return (
                <tr key={r.id} className="border-t">
                  <td className="p-3 font-medium">{r.query}</td>
                  <td className="p-3"><Badge variant="outline">{r.store}</Badge></td>
                  <td className="p-3 text-muted-foreground">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="p-3">{expired ? <Badge variant="destructive">Expired</Badge> : <span className="text-muted-foreground">{new Date(r.expires_at).toLocaleString()}</span>}</td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="ghost" disabled={busy === r.id} onClick={() => removeOne(r.id)}>
                      {busy === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}